<template>
    <BaseForm>
        <template #fields>
            <MultiLinkGroup>
                <MultiLink
                    :label="$t('competition-national-label')"
                    :help-button-text="$t('competition-national-help')"
                    :links="values.competitionNational"
                    :error-messages="errors.competitionNational"
                    @input="setFieldValue('competitionNational', $event)"
                    @delete="setFieldValue('competitionNational', $event)"
                />
                <MultiLink
                    :label="$t('other-competition-national')"
                    :help-button-text="$t('other-competition-national-help')"
                    :links="values.otherCompetitionNational"
                    :error-messages="errors.otherCompetitionNational"
                    @input="setFieldValue('otherCompetitionNational', $event)"
                    @delete="setFieldValue('otherCompetitionNational', $event)"
                />
                <MultiLink
                    :label="$t('competition-international-label')"
                    :help-button-text="$t('competition-international-help')"
                    :links="values.competitionInternational"
                    :error-messages="errors.competitionInternational"
                    @input="setFieldValue('competitionInternational', $event)"
                    @delete="setFieldValue('competitionInternational', $event)"
                />
                <MultiLink
                    :label="$t('other-competition-international')"
                    :help-button-text="$t('other-competition-international-help')"
                    :links="values.otherCompetitionInternational"
                    :error-messages="errors.otherCompetitionInternational"
                    @input="setFieldValue('otherCompetitionInternational', $event)"
                    @delete="setFieldValue('otherCompetitionInternational', $event)"
                />
            </MultiLinkGroup>
        </template>
    </BaseForm>
</template>
<script setup lang="ts">
import BaseForm from '@/components/Form/BaseForm.vue';
import { useMultiLink } from '@/components/MultiLink/composables/useMultiLink';
import MultiLink from '@/components/MultiLink/MultiLink.vue';
import MultiLinkGroup from '@/components/MultiLink/MultiLinkGroup.vue';
import { useEventInjection } from '@/event/composables/useEventInjection';
import { useForm } from '@/forms/composables/useForm';
import { computed } from 'vue';
import { EventSeriesCompetitionSchema } from '../schemas';

const { event, createEventMultiLink } = useEventInjection();
const { getFormFieldLinks, saveMultiLinkFields } = useMultiLink(createEventMultiLink);

const competitionNationalId = computed(() => event.value?.competitionNational?.id);
const otherCompetitionNationalId = computed(() => event.value?.otherCompetitionNational?.id);
const competitionInternationalId = computed(() => event.value?.competitionInternational?.id);
const otherCompetitionInternationalId = computed(() => event.value?.otherCompetitionInternational?.id);

const competitionNationalLinks = computed(() => event.value?.competitionNational?.links.nodes ?? []);
const otherCompetitionNationalLinks = computed(() => event.value?.otherCompetitionNational?.links.nodes ?? []);
const competitionInternationalLinks = computed(() => event.value?.competitionInternational?.links.nodes ?? []);
const otherCompetitionInternationalLinks = computed(
    () => event.value?.otherCompetitionInternational?.links.nodes ?? [],
);

const { errors, values, setFieldValue, validate, onDiscard, resetForm } = useForm(
    EventSeriesCompetitionSchema,
    computed(() => ({
        competitionNational: getFormFieldLinks(competitionNationalLinks),
        otherCompetitionNational: getFormFieldLinks(otherCompetitionNationalLinks),
        competitionInternational: getFormFieldLinks(competitionInternationalLinks),
        otherCompetitionInternational: getFormFieldLinks(otherCompetitionInternationalLinks),
    })),
);

async function save() {
    const valid = await validate();
    if (valid) {
        await saveMultiLinkFields([
            {
                id: competitionNationalId.value,
                fieldName: 'competitionNational',
                links: values.value.competitionNational,
            },
            {
                id: otherCompetitionNationalId.value,
                fieldName: 'otherCompetitionNational',
                links: values.value.otherCompetitionNational,
            },
            {
                id: competitionInternationalId.value,
                fieldName: 'competitionInternational',
                links: values.value.competitionInternational,
            },
            {
                id: otherCompetitionInternationalId.value,
                fieldName: 'otherCompetitionInternational',
                links: values.value.otherCompetitionInternational,
            },
        ]);
    }
}

defineExpose({ save, onDiscard, resetForm });
</script>
